import { Typography } from "antd";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getContractListThunk, getContractTypesListThunk } from "tm-shared/api";
import { Contract, UserRole } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { Loading } from "@/components/Loading";
import Meta from "@/components/Meta";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useMarket } from "@/hooks/useMarket";

import {
    StyledAPContainer,
    StyledAPContractsTable,
    StyledAPContractsTableRow,
    StyledAPContractsTableRowLeft,
} from "../../Styled";

interface IEnrichedContract extends Contract {
    isExpired: boolean;
}

const isContractExpired = (endDate: string | null | undefined): boolean => {
    return endDate ? dayjs(endDate).isBefore(dayjs(), "day") : false;
};

const ContractRow: FC<{
    contract: IEnrichedContract;
    marketId: string;
    contractTypeName: string;
}> = ({ contract, marketId, contractTypeName }) => {
    const { id, name, endDate, isExpired } = contract;

    return (
        <StyledAPContractsTableRow
            key={id}
            to={`/work-plan/details/${marketId}/contracts/${id}`}
        >
            <StyledAPContractsTableRowLeft isExpired={isExpired}>
                <PrimaryText fontSize={14} fontWeight="400">
                    {name}
                </PrimaryText>
                <Typography.Text type="secondary">
                    {contractTypeName}
                </Typography.Text>
            </StyledAPContractsTableRowLeft>

            <PrimaryText
                fontSize={14}
                fontWeight="400"
                style={{ maxWidth: "100px" }}
            >
                {endDate
                    ? `до ${dayjs(endDate).format("DD.MM.YYYY")}`
                    : "Безстроково"}
            </PrimaryText>
        </StyledAPContractsTableRow>
    );
};

const InfoTabContracts = () => {
    const { marketId = "" } = useParams<{ marketId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(true);

    const { role } = useAppSelector((state) => state.user);
    const { targetsContracts, contractTypesList } = useAppSelector(
        (state) => state.offline,
    );

    const { market, marketLoading } = useMarket(marketId);

    const allContracts = useMemo(() => {
        if (!market || !targetsContracts) return [];

        const contractSources = [
            marketId,
            market.storage?.id,
            market.contragent?.id,
            market.address?.mainRegionId,
            market.networkId,
        ].filter(Boolean);

        const contracts: Contract[] = [];
        const seenIds = new Set<string>();

        contractSources.forEach((sourceId) => {
            const sourceContracts = targetsContracts[sourceId!];
            if (sourceContracts) {
                Object.values(sourceContracts).forEach((contract) => {
                    if (contract && !seenIds.has(contract.id)) {
                        contracts.push(contract);
                        seenIds.add(contract.id);
                    }
                });
            }
        });

        return contracts;
    }, [market, targetsContracts, marketId]);

    const enrichedContracts = useMemo<IEnrichedContract[]>(
        () =>
            allContracts.map((contract) => ({
                ...contract,
                isExpired: isContractExpired(contract.endDate),
            })),
        [allContracts],
    );

    useEffect(() => {
        if (!marketId) return;

        const loadContracts = async () => {
            setIsLoading(true);
            try {
                await Promise.all([
                    dispatch(
                        getContractListThunk({
                            marketId: marketId,
                            isActive:
                                role === UserRole.Ta || role === UserRole.Md,
                        }),
                    ),
                    dispatch(getContractTypesListThunk()),
                ]);
            } catch (error) {
                console.error("Error loading contracts:", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadContracts();
    }, [marketId, role, dispatch]);

    const isSupervisor = role === UserRole.Sv;
    const hasContracts = enrichedContracts.length > 0;

    if (isLoading || marketLoading) {
        return (
            <>
                <Meta title="TT Info Contracts" />
                <Header title="Контракти" back titleFontSize={16} />
                <StyledAPContainer>
                    <Loading />
                </StyledAPContainer>
            </>
        );
    }

    return (
        <>
            <Meta title="TT Info Contracts" />
            <Header title="Контракти" back titleFontSize={16} />

            <StyledAPContainer>
                {hasContracts ? (
                    <StyledAPContractsTable>
                        {enrichedContracts.map((contract) => (
                            <ContractRow
                                key={contract.id}
                                contract={contract}
                                marketId={marketId}
                                contractTypeName={
                                    contractTypesList?.[contract.type] ||
                                    "Невідомий вид контракту"
                                }
                            />
                        ))}
                    </StyledAPContractsTable>
                ) : (
                    <PrimaryText
                        fontSize={14}
                        fontWeight="400"
                        style={{ textAlign: "center" }}
                    >
                        Контрактів по даній ТТ не знайдено
                    </PrimaryText>
                )}
                {isSupervisor && (
                    <BaseButton
                        onClick={() => navigate(`/addContract/${marketId}`)}
                        type="primary"
                        marginTop="16px"
                    >
                        Додати контракт
                    </BaseButton>
                )}
            </StyledAPContainer>
        </>
    );
};

export default InfoTabContracts;
